import React, { useState } from 'react';
import {
    Grid,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Chip,
    Button,
    Box,
    Collapse,
    IconButton
} from '@material-ui/core';
import { ExpandMoreIcon, ExpandLessIcon, DownloadIcon } from '../../../../icons';
import { useSwaggerClient } from '../../../../hooks/useSwaggerClient';

// Alias for backward compatibility
const ExpandMore = ExpandMoreIcon;
const ExpandLess = ExpandLessIcon;
const GetApp = DownloadIcon;

const ActivitiesTable = ({ agentId, fromDate, toDate, dateType, status }) => {
    const [expanded, setExpanded] = useState(true);
    const [dashboardData, loading] = useSwaggerClient(
        'users/statistics',
        'getAgentActivityDashboard',
        {
            agentId,
            fromDate,
            toDate,
            dateType,
            status
        },
        [agentId, fromDate, toDate, dateType, status]
    );

    const activities = dashboardData?.activities || [];

    const handleExport = () => {
        // Create CSV content
        const headers = [
            'Activity Name',
            'Total Vouchers',
            'Confirmed',
            'Unconfirmed',
            'Cancelled',
            'Total Amount ($)',
            'Commission ($)',
            'Total Guests'
        ];
        const csvRows = [headers.join(',')];

        activities.forEach(activity => {
            const row = [
                `"${activity.activityName}"`,
                activity.totalVouchers,
                activity.confirmed,
                activity.unconfirmed,
                activity.cancelled,
                activity.totalAmount.toFixed(2),
                activity.commission.toFixed(2),
                activity.guests.length
            ];
            csvRows.push(row.join(','));
        });

        // Create and download file
        const csvContent = csvRows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `agent_activities_${fromDate}_to_${toDate}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (loading) {
        return (
            <Grid item xs={12}>
                <Card>
                    <CardContent style={{ textAlign: 'center', padding: '40px' }}>
                        <CircularProgress />
                    </CardContent>
                </Card>
            </Grid>
        );
    }

    if (activities.length === 0) {
        return (
            <Grid item xs={12}>
                <Card>
                    <CardContent>
                        <Typography color="textSecondary">
                            No activities found for the selected date range.
                        </Typography>
                    </CardContent>
                </Card>
            </Grid>
        );
    }

    return (
        <Grid item xs={12}>
            <Card>
                <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">
                            Activities Breakdown ({activities.length} activities)
                        </Typography>
                        <Box>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<GetApp />}
                                onClick={handleExport}
                                style={{ marginRight: 8 }}
                            >
                                Export CSV
                            </Button>
                            <IconButton onClick={() => setExpanded(!expanded)}>
                                {expanded ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                        </Box>
                    </Box>
                    <Collapse in={expanded}>
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Activity Name</TableCell>
                                        <TableCell align="center">Total Vouchers</TableCell>
                                        <TableCell align="center">Confirmed</TableCell>
                                        <TableCell align="center">Unconfirmed</TableCell>
                                        <TableCell align="center">Cancelled</TableCell>
                                        <TableCell align="right">Total Amount</TableCell>
                                        <TableCell align="right">Commission</TableCell>
                                        <TableCell align="center">Total Guests</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {activities.map(activity => (
                                        <TableRow key={activity.activityId}>
                                            <TableCell component="th" scope="row">
                                                {activity.activityName}
                                            </TableCell>
                                            <TableCell align="center">
                                                <strong>{activity.totalVouchers}</strong>
                                            </TableCell>
                                            <TableCell align="center">
                                                {activity.confirmed > 0 && (
                                                    <Chip
                                                        label={activity.confirmed}
                                                        color="primary"
                                                        size="small"
                                                    />
                                                )}
                                                {activity.confirmed === 0 && '-'}
                                            </TableCell>
                                            <TableCell align="center">
                                                {activity.unconfirmed > 0 && (
                                                    <Chip
                                                        label={activity.unconfirmed}
                                                        color="default"
                                                        size="small"
                                                    />
                                                )}
                                                {activity.unconfirmed === 0 && '-'}
                                            </TableCell>
                                            <TableCell align="center">
                                                {activity.cancelled > 0 && (
                                                    <Chip
                                                        label={activity.cancelled}
                                                        color="secondary"
                                                        size="small"
                                                    />
                                                )}
                                                {activity.cancelled === 0 && '-'}
                                            </TableCell>
                                            <TableCell align="right">
                                                ${activity.totalAmount.toFixed(2)}
                                            </TableCell>
                                            <TableCell align="right">
                                                <strong>${activity.commission.toFixed(2)}</strong>
                                            </TableCell>
                                            <TableCell align="center">
                                                <strong>{activity.guests.length}</strong>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Collapse>
                </CardContent>
            </Card>
        </Grid>
    );
};

export default ActivitiesTable;
